import React, { useState, useEffect, useRef } from "react";
import {
  Typography,
  Paper,
  Box,
  Modal,
  Button,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import IconSearch from "../../assets/svg/search-icon-grey.svg";
import axiosInstant from "../../api";
import ShortlistProCard from "../admin/Shortlists/ShortlistProCard";
import { ISinglePro } from "../../components/singlePro/state";
import { DotLottleLoader, LoadMoreBtn } from "../../components/Loader";
import { NoDataView } from "../../components/EmptyPanel/NoDataView";
import { IconUser } from "../../utils/icons";

interface Props {
  onClose: () => void;
  onSave: (selectedExperts: number[]) => void;
  experts: ISinglePro[];
}

const LIMIT = 20;
const apiProps = { limit: LIMIT, status: "Active" };

export default function UpdateExpertListModal({
  onClose,
  experts,
  onSave,
}: Props) {
  const timeoutId = useRef<NodeJS.Timeout>();
  const prevProIds = useRef(experts.map((pro) => pro.userid));

  const [expertsList, setExpertsList] = useState<ISinglePro[]>([]);
  const [selectedExperts, setSelectedExperts] = useState(
    experts.map((expert) => expert.userid)
  );
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [search, setSearch] = useState("");

  const fetchExperts = async () => {
    setIsLoading(true);

    try {
      const response = await axiosInstant.get(`/users/pros`, {
        params: { ...apiProps, page, search },
      });
      const responseData = response.data.rows || [];

      setHasMore(responseData.length && responseData.length >= LIMIT);

      const filteredList = (
        page === 1 ? responseData : [...expertsList, ...responseData]
      ).filter(
        (pro: ISinglePro) =>
          pro.userid && !prevProIds.current.includes(pro.userid)
      );
      setExpertsList(filteredList);
    } catch (error) {
      console.error("Error fetching experts:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleSelectExpert = async (expertId: number, isChecked: boolean) => {
    if (isChecked) {
      setSelectedExperts((prev) => [...prev, expertId]);
    } else {
      setSelectedExperts((prev) => prev.filter((id) => id !== expertId));
    }
  };

  const handleSave = () => {
    const validIds = selectedExperts.filter((id) => id !== undefined);
    onSave((validIds as any) || []);
    onClose();
  };

  useEffect(() => {
    clearTimeout(timeoutId.current);
    timeoutId.current = setTimeout(() => {
      fetchExperts();
    }, 400);
    return () => {
      clearTimeout(timeoutId.current);
      timeoutId.current = undefined;
    };
  }, [search, page]);

  const hasNewSelections = selectedExperts.some(
    (id) => !prevProIds.current.includes(id)
  );

  return (
    <Modal open onClose={onClose}>
      <Box
        component={Paper}
        className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 max-h-[90vh] bg-surface rounded-xl box-shadow-24 flex flex-col w-[931px] overflow-y-auto"
      >
        <Box className="flex flex-col sticky top-0 bg-surface z-10 w-full">
          <Box className="flex justify-between items-center sticky top-0 bg-surface z-10 py-4 w-full px-6">
            <div className="flex items-center w-100 gap-20">
              <Typography className="text-text-1 !text-2xl !font-bold whitespace-nowrap">
                Experts
              </Typography>

              <div className="w-full relative">
                <img
                  className="absolute top-1/2 transform -translate-x-1/2 -translate-y-1/2 left-5"
                  alt="search-icon"
                  src={IconSearch}
                />
                <input
                  type="text"
                  className="w-[333px] h-[40px] px-[2.25rem] py-0 gap-2 bg-surface-2 text-text-2 placeholder-muted rounded-xl focus:outline-none focus:text-text-1"
                  id="search-experts"
                  placeholder="Search"
                  onChange={handleSearchChange}
                />
              </div>
            </div>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
          <hr className="border-border-token" />
        </Box>
        <Box className="w-full flex flex-col h-[500px] max-h-[80vh] overflow-y-auto">
          {((isLoading && page !== 1) || !isLoading) &&
            expertsList.map((pro) => (
              <ShortlistProCard
                key={pro.userid}
                pro={pro}
                isSelected={selectedExperts.includes(pro.userid)}
                onSelect={handleSelectExpert}
              />
            ))}
          {isLoading && page === 1 && (
            <Box className="flex justify-center mt-3">
              <DotLottleLoader />
            </Box>
          )}
          {!isLoading && !expertsList.length && (
            <div className="flex flex-col items-center justify-center gap-7 h-full w-full">
              <div className="flex items-center justify-center p-2 border-muted rounded-full border text-muted">
                <IconUser width={24} height={24} stroke="currentColor" strokeWidth={1.5} />
              </div>
              <div className="text-text-2 font-circular text-[14px] font-[450] leading-normal not-italic text-center">
                No experts found
              </div>
            </div>
          )}
          {hasMore && (
            <LoadMoreBtn onClick={() => setPage((prevPage) => prevPage + 1)} loading={isLoading} customClasses="!my-3" />
          )}
        </Box>
        {!isLoading && expertsList.length > 0 && (
          <Box className="flex items-center justify-end py-5 bg-surface px-6">
            <Button
              variant="contained"
              onClick={handleSave}
              size="large"
              disabled={!hasNewSelections}
            >
              Update
            </Button>
          </Box>
        )}
      </Box>
    </Modal>
  );
}
